import { DownOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import { useControllableValue } from 'ahooks';
import {
  Dropdown,
  Input,
  Space,
  Tooltip,
} from 'antd';
import { isEmpty } from 'lodash-es';

import React, { CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaFieldsPanelProps } from '@/presentation/components/form';
import { Zone } from '@/proto/field';
import { TdsFieldSelectorProps } from '../tds-field-selector';
import { BaseFormItemProps } from '../type';
import { TdsAreaEditor } from './editor';
import styles from './index.module.scss';
import { AreaFieldValue } from './types';

export type { AreaFieldValue } from './types';
export { AreaNameEnum } from './types';


export interface TdsAreaFieldsPanelProps extends BaseFormItemProps<any> {
  className?: string
  style?: CSSProperties;
  fieldScope?: Zone; // 旧协议：可选，兼容旧代码（如果提供则使用）
  valueSelectProps?: TdsFieldSelectorProps; // 新协议：可选，支持通过 valueSelectProps.fieldScope 传递（优先使用）
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}


const getFilterText = (values: AreaFieldValue[], separator: string) => values.map((item) => {
  if (Array.isArray(item?.values)) {
    return `[${item?.values?.join(separator)}]`;
  }
  return `[${item?.values}]`;
}).join(' / ');


export const TdsArea: React.FC<TdsAreaFieldsPanelProps> = (props) => {
  const { t } = useTranslation();
  const { className, fieldScope: fieldScopeFromProps, valueSelectProps, prefix, suffix } = props;
  const fieldScope = valueSelectProps?.fieldScope || fieldScopeFromProps;
  const [value, setValue] = useControllableValue<AreaFieldValue[]>(props, {
    defaultValue: [],
  });
  const [visible, setVisible] = useState(false);

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const filterText = useMemo(() => {
    if (!value || isEmpty(value)) return '';
    return getFilterText(value, t('、'));
  }, [t, value]);

  return (
    <div className={className ? className : styles.panel} style={props.style}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder={t('请选择')}
            onFocus={showOverlay}
            prefix={prefix}
            suffix={suffix ?? <DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
            style={{ width: '100%' }}
            value={filterText}
          />
        </Tooltip>
        <Dropdown
          overlay={(
            <TdsAreaEditor
              value={value}
              fieldScope={fieldScope}
              loadOnMount={visible}
              onConfirm={(filtered) => {
                hideOverlay();
                setValue(filtered);
              }}
              onCancel={hideOverlay}
            />
          )}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={v => setVisible(v)}
        >
          <div style={{ height: 1 }}/>
        </Dropdown>
      </Space>
    </div>
  );
};


export const AreaForm: React.FC<AreaFieldsPanelProps> = (props) => {
  const { name, label } = props;
  return (
    <ProFormField name={name} label={label}>
      <TdsArea
        {...props}
      />
    </ProFormField>
  );
};
